import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import DrawingLayer from '@component/gridgio/workspace/workspace-canvas/drawing-layer';
import AnnotationLayer from '@component/gridgio/workspace/workspace-canvas/annotation-layer';
import { useWorkspaceGesture } from '@hook/gridgio/useWorkspace';
import type { IAnnotation, IArea, IDrawing, IWorkspaceView, TAreaType } from '@datatype/gridgio/type';

interface IProps {
  drawing: IDrawing;
  page: number;
  rotation: number;
  areaType: TAreaType | null;
  workArea: IArea | null;
  keyPlanArea: IArea | null;
  cropArea: IArea | null;
  selectedIssueId: string | null;
  selectedAnnotationId: string | null;
  annotationSelectionEnabled: boolean;
  annotation: IAnnotation[];
  view: IWorkspaceView;
  moveActive: boolean;
  handleViewChange: React.Dispatch<React.SetStateAction<IWorkspaceView>>;
  handleAreaChange: (type: TAreaType, area: IArea) => void;
  handleAnnotationComplete: (issueId: string, mark: IAnnotation['mark']) => void;
  handleAnnotationSelect: (annotationId: string | null) => void;
  handlePageChange: (totalPage: number) => void;
}

const Wrapper = styled.div<{ $moveActive: boolean }>`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: ${({ $moveActive }) => ($moveActive ? 'grab' : 'default')};
  touch-action: none;
  background-color: ${color.GRAY_3};
  background-image:
    linear-gradient(${color.GRAY_2}40 1px, transparent 1px),
    linear-gradient(90deg, ${color.GRAY_2}40 1px, transparent 1px);
  background-size: 24px 24px;
`;

const LayerContainer = styled.div<{ $width: number; $height: number; $view: IWorkspaceView }>`
  position: relative;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  transform: ${({ $view }) => `translate(${$view.x}px, ${$view.y}px) scale(${$view.zoom})`};
  transform-origin: center;
`;

const WorkspaceCanvas: React.FC<IProps> = ({
  drawing,
  page,
  rotation,
  areaType,
  workArea,
  keyPlanArea,
  cropArea,
  selectedIssueId,
  selectedAnnotationId,
  annotationSelectionEnabled,
  annotation,
  view,
  moveActive,
  handleViewChange,
  handleAreaChange,
  handleAnnotationComplete,
  handleAnnotationSelect,
  handlePageChange,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const handleSizeChange = useCallback((width: number, height: number) => setCanvasSize({ width, height }), []);
  const { handlePointerDown, handlePointerMove, handlePointerEnd } = useWorkspaceGesture({
    viewportRef: wrapperRef,
    moveActive,
    handleViewChange,
  });

  return (
    <Wrapper
      ref={wrapperRef}
      aria-label={drawing.name}
      $moveActive={moveActive && !areaType && !selectedIssueId}
      onPointerDownCapture={areaType || selectedIssueId ? undefined : handlePointerDown}
      onPointerMoveCapture={areaType || selectedIssueId ? undefined : handlePointerMove}
      onPointerUpCapture={areaType || selectedIssueId ? undefined : handlePointerEnd}
      onPointerCancelCapture={areaType || selectedIssueId ? undefined : handlePointerEnd}>
      <LayerContainer $width={canvasSize.width} $height={canvasSize.height} $view={view}>
        <DrawingLayer
          drawing={drawing}
          page={page}
          viewportRef={wrapperRef}
          rotation={rotation}
          cropArea={cropArea}
          handleSizeChange={handleSizeChange}
          handlePageChange={handlePageChange}
        />
        <AnnotationLayer
          key={page}
          areaType={areaType}
          workArea={workArea}
          keyPlanArea={keyPlanArea}
          cropArea={cropArea}
          selectedIssueId={selectedIssueId}
          selectedAnnotationId={selectedAnnotationId}
          selectionEnabled={annotationSelectionEnabled && !areaType && !selectedIssueId && !moveActive}
          annotation={annotation}
          handleAreaChange={handleAreaChange}
          handleAnnotationComplete={handleAnnotationComplete}
          handleAnnotationSelect={handleAnnotationSelect}
        />
      </LayerContainer>
    </Wrapper>
  );
};

export default WorkspaceCanvas;
